import { KatIcon } from "@amzn/katal-react";
import classnames from "classnames";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import type { MismatchTableData } from "src/api/interfaces/validation_result";
import AlertItem from "src/components/Alert/AlertItem";
import { useValidationResults } from "src/components/Contexts/ValidationResultsContext";
import DataTable from "src/components/DataTable";
import type { DataTableDefinition } from "src/components/DataTable/DataTableDefinition";
import MismatchCurrentErrorsBadge from "src/components/Pages/DocumentInbound/Tabs/Validations/ValidationsModals/Mismatch/MismatchCurrentErrorsBadge";
import MismatchTableFilterBar from "src/components/Pages/DocumentInbound/Tabs/Validations/ValidationsModals/Mismatch/MismatchTableFilterBar";
import type {
  MismatchTableRowSorter,
  MismatchTableTransformer,
} from "src/components/Pages/DocumentInbound/Tabs/Validations/ValidationsModals/Mismatch/types";
import ValidationSeverityDescriptionModal from "src/components/Pages/DocumentInbound/ValidationSeverityDescription/ValidationSeverityDescriptionModal";
import {
  ValidationUserActionsDropdown,
  type ValidationUserActionsDropdownProps,
} from "src/components/UserActions/ValidationUserActionsDropdown";
import styles from "src/styles.module.scss";

const ALERT_LABEL = "aic_mismatch_exceeds_size_limit_alert";

export interface MismatchTableProps<T> {
  readonly dataTableDefinition: DataTableDefinition<T>;
  readonly dataTransformer: MismatchTableTransformer<T>;
  readonly rowSorter?: MismatchTableRowSorter<T>;
  readonly validationGroupId: string;
  readonly validationUserActionsDropdownProps?: ValidationUserActionsDropdownProps;
}

export default function MismatchTable<T>({
  dataTableDefinition,
  dataTransformer,
  rowSorter,
  validationGroupId,
  validationUserActionsDropdownProps,
}: MismatchTableProps<T>) {
  const { t } = useTranslation();
  const { validationResultsByGroup } = useValidationResults();
  const validationResults =
    validationResultsByGroup[validationGroupId]?.validationResults || [];
  const exceedsSizeLimits =
    validationResultsByGroup[validationGroupId]?.exceedsSizeLimits || false;

  const mismatchTableData: MismatchTableData<T> = {
    mismatchRowData: validationResults.map((validationResult, index) =>
      dataTransformer(validationResult, index, t)
    ),
  };

  if (rowSorter) {
    mismatchTableData.mismatchRowData.sort(rowSorter);
  }

  const [filteredMismatchRowData, setFilteredMismatchRowData] = useState(
    mismatchTableData.mismatchRowData
  );

  return (
    <>
      {exceedsSizeLimits && (
        <AlertItem description={t(ALERT_LABEL)} variant="warning" />
      )}
      {validationUserActionsDropdownProps && (
        <div className={classnames(styles.row_flex_non_wrap_space_between)}>
          <div className={classnames(styles.row_flex_non_wrap_center_gap_10)}>
            <MismatchTableFilterBar
              mismatchRowData={mismatchTableData.mismatchRowData}
              setFilteredMismatchRowData={setFilteredMismatchRowData}
            />
            <ValidationSeverityDescriptionModal
              ClickableComponent={({ onClick }) => (
                <KatIcon
                  onClick={onClick}
                  className={classnames(styles.info, styles.pointer)}
                  slot="action"
                  name="info_outline"
                  size="small"
                />
              )}
            />
          </div>

          <div className={classnames(styles.row_flex_non_wrap_center_gap_10)}>
            <MismatchCurrentErrorsBadge
              currentErrors={filteredMismatchRowData.length}
              totalErrors={mismatchTableData.mismatchRowData.length}
            />
            <ValidationUserActionsDropdown
              userActions={validationUserActionsDropdownProps.userActions}
              onActionSelected={
                validationUserActionsDropdownProps.onActionSelected
              }
            />
          </div>
        </div>
      )}

      <DataTable
        id="mismatch-table"
        tableDefinition={dataTableDefinition}
        contents={filteredMismatchRowData}
      />
    </>
  );
}
